import { levenshteinDistance } from "utils/strings";

/**
 * The set of schema columns that the CSV auto-mapper can recognise and suggest.
 * Kept here rather than in the hook layer because it is a UI/parsing concern,
 * not an API transport concern.
 */
export type KnownSchemaColumn =
  | "uwi"
  | "name"
  | "md"
  | "tvd"
  | "kb"
  | "inclination"
  | "azimuth"
  | "dx"
  | "dy"
  | "x"
  | "y"
  | "northRef"
  | "compMethod"
  | "northDecl"
  | "subsea"
  | "status";

// ─── Legacy single-file upload ───────────────────────────────────────────────

/**
 * Used by the legacy single-file upload format.
 *
 * Valid UTM zone pattern: a number 1–60 followed by N or S (hemisphere letter).
 * WGS84 supports both northern (N) and southern (S) hemispheres. NAD83 and
 * NAD27 are defined only for North America and therefore only valid with N;
 * that constraint is validated server-side.
 * Examples: 1N, 11N, 60N, 30S
 * Exported so validation logic and tests share one definition.
 */
export const UTM_ZONE_PATTERN = /^([1-9]|[1-5][0-9]|60)[NS]$/i;

/**
 * Parses the first line of a CSV file into an array of header strings,
 * correctly handling RFC 4180 quoted fields (including commas inside quotes
 * and escaped double-quotes via "").
 */
export const parseCsvHeaderLine = (line: string): string[] => {
  const headers: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped double-quote inside a quoted field
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      headers.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  headers.push(current.trim());

  return headers
    .map((header) => header.replace(/^"(.*)"$/s, "$1").trim())
    .filter(Boolean);
};

/**
 * Number of preamble rows that precede the column header row in both the
 * surface locations file and the survey stations file. Per the spec:
 *   Row 1 – Coordinate system
 *   Row 2 – Date
 *   Row 3 – Surface Units
 *   Row 4 – Depth Units
 *   Row 5 – Azimuth Reference
 *   Row 6 – Column headers  ← this is what we want
 */
const CSV_PREAMBLE_ROW_COUNT = 5;

/**
 * Reads the column header row (row 6) from a two-file-format CSV and returns
 * the parsed column names. The first five rows are the fixed metadata preamble
 * and are skipped.
 *
 * Uses a generous read window so that non-ASCII (multi-byte UTF-8) header
 * names are not truncated mid-sequence; the header row is extracted from the
 * decoded text, not from a raw byte slice.
 */
export const extractCsvHeaders = (file: File): Promise<string[]> =>
  new Promise((resolve, reject) => {
    // Read enough bytes to safely cover the preamble plus any realistic header
    // row, including multi-byte UTF-8 sequences without byte-boundary splits.
    const READ_WINDOW_BYTES = 65_536;
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = (e.target?.result as string) || "";
      const lines = text.split(/\r?\n/);
      // Row 6 is at index 5 after skipping the 5-row preamble.
      const headerLine = lines[CSV_PREAMBLE_ROW_COUNT] ?? "";
      resolve(parseCsvHeaderLine(headerLine));
    };

    reader.onerror = () => reject(new Error("Failed to read CSV file"));

    // Slice after readAsText so the browser decodes the chunk as UTF-8,
    // preventing byte-boundary splits inside multi-byte characters.
    reader.readAsText(file.slice(0, READ_WINDOW_BYTES));
  });

/**
 * Reads the first line of a legacy single-file CSV and returns the parsed
 * column names. Unlike `extractCsvHeaders`, this function does NOT skip any
 * preamble rows — the first line is assumed to be the column header.
 */
export const extractLegacyCsvHeaders = (file: File): Promise<string[]> =>
  new Promise((resolve, reject) => {
    const READ_WINDOW_BYTES = 65_536;
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = (e.target?.result as string) || "";
      const lines = text.split(/\r?\n/);
      const headerLine = lines[0] ?? "";
      resolve(parseCsvHeaderLine(headerLine));
    };

    reader.onerror = () => reject(new Error("Failed to read CSV file"));
    reader.readAsText(file.slice(0, READ_WINDOW_BYTES));
  });

/**
 * Used by the legacy single-file CSV upload to define mappable columns.
 *
 * The subset of schema columns required to compute well survey geometry.
 * These are the only columns surfaced in the mapping UI.
 *
 * The API requires:
 * - always: uwi, md, inclination, azimuth, x, y
 * - and one depth path:
 *   - subsea, or
 *   - kb + tvd
 */
export const SURVEY_REQUIRED_COLUMNS: readonly {
  value: KnownSchemaColumn;
  label: string;
  aliases: readonly string[];
}[] = [
  {
    value: "uwi",
    label: "UWI",
    aliases: ["uwi", "well id", "wellid", "well_id", "unique well identifier", "api"]
  },
  {
    value: "md",
    label: "MD (Measured Depth)",
    aliases: [
      "md",
      "measured depth",
      "measured_depth",
      "measdepth",
      "meas depth",
      "depth"
    ]
  },
  {
    value: "tvd",
    label: "TVD (True Vertical Depth)",
    aliases: [
      "tvd",
      "true vertical depth",
      "true_vertical_depth",
      "tvdepth",
      "tv depth",
      "vert depth",
      "vertical depth"
    ]
  },
  {
    value: "kb",
    label: "KB (Kelly Bushing)",
    aliases: [
      "kb",
      "kelly bushing",
      "kelly_bushing",
      "kellybushing",
      "kb elevation",
      "kb_elevation",
      "rig floor",
      "rkb"
    ]
  },
  {
    value: "subsea",
    label: "Subsea",
    aliases: [
      "subsea",
      "sub sea",
      "sub_sea",
      "subsea depth",
      "subsea_depth",
      "ss",
      "tvdss",
      "tvd ss"
    ]
  },
  {
    value: "inclination",
    label: "Inclination",
    aliases: ["inclination", "incl", "inc", "inclination angle", "dip", "angle"]
  },
  {
    value: "azimuth",
    label: "Azimuth",
    aliases: ["azimuth", "az", "azi", "bearing", "direction", "azim"]
  },
  {
    value: "x",
    label: "X (Easting / Surface)",
    aliases: [
      "x",
      "easting",
      "surface x",
      "surface_x",
      "x coord",
      "x_coord",
      "longitude",
      "lon",
      "long",
      "utm_x",
      "utm x"
    ]
  },
  {
    value: "y",
    label: "Y (Northing / Surface)",
    aliases: [
      "y",
      "northing",
      "surface y",
      "surface_y",
      "y coord",
      "y_coord",
      "latitude",
      "lat",
      "utm_y",
      "utm y"
    ]
  }
] as const;

export type SurveyRequiredColumn = (typeof SURVEY_REQUIRED_COLUMNS)[number];

/**
 * Maximum normalised Levenshtein distance ratio accepted as a fuzzy match.
 * A ratio of 0.35 allows for moderate typos / abbreviations while rejecting
 * clearly unrelated strings (e.g. "md" → "tvd" would score ~0.67 and be rejected).
 */
const MAX_LEVENSHTEIN_RATIO = 0.35;

const normalise = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[_\s.-]+/g, " ")
    .replace(/\s+/g, " ");

/**
 * Checks whether a candidate string is an exact or alias match for a schema column.
 */
const isAliasMatch = (candidate: string, column: SurveyRequiredColumn): boolean => {
  const norm = normalise(candidate);
  return column.aliases.some((alias) => normalise(alias) === norm);
};

/**
 * Computes a distance ratio in [0, 1] between two normalised strings.
 * 0 = identical, 1 = completely different.
 */
const levenshteinRatio = (a: string, b: string): number => {
  const normA = normalise(a);
  const normB = normalise(b);
  const maxLen = Math.max(normA.length, normB.length);
  if (maxLen === 0) return 0;
  return levenshteinDistance(normA, normB) / maxLen;
};

/**
 * Used by the legacy single-file CSV upload.
 *
 * Auto-maps an array of CSV headers to required schema columns.
 * Each header is matched independently; no two headers will map to the same
 * schema column — the first (closest) match wins and the column is removed
 * from the available pool.
 *
 * Returns a `Map<csvHeader, KnownSchemaColumn>` containing only the headers
 * that could be confidently matched.
 */
export const autoMapCsvHeaders = (
  csvHeaders: readonly string[]
): Map<string, KnownSchemaColumn> => {
  const result = new Map<string, KnownSchemaColumn>();
  const claimed = new Set<KnownSchemaColumn>();

  type Candidate = {
    header: string;
    column: KnownSchemaColumn;
    ratio: number;
    isAlias: boolean;
    headerIndex: number;
    aliasLengthDelta: number;
  };

  const candidates: Candidate[] = [];

  for (const [headerIndex, header] of csvHeaders.entries()) {
    for (const col of SURVEY_REQUIRED_COLUMNS) {
      if (isAliasMatch(header, col)) {
        const normalisedHeader = normalise(header);
        const exactAlias = col.aliases.find(
          (alias) => normalise(alias) === normalisedHeader
        );
        candidates.push({
          header,
          column: col.value,
          ratio: 0,
          isAlias: true,
          headerIndex,
          aliasLengthDelta: Math.abs((exactAlias ?? "").length - header.length)
        });
        continue;
      }

      let bestRatio = Infinity;
      let bestAliasLengthDelta = Infinity;

      for (const alias of col.aliases) {
        const ratio = levenshteinRatio(header, alias);
        const aliasLengthDelta = Math.abs(
          normalise(alias).length - normalise(header).length
        );

        if (
          ratio < bestRatio ||
          (ratio === bestRatio && aliasLengthDelta < bestAliasLengthDelta)
        ) {
          bestRatio = ratio;
          bestAliasLengthDelta = aliasLengthDelta;
        }
      }

      if (bestRatio <= MAX_LEVENSHTEIN_RATIO) {
        candidates.push({
          header,
          column: col.value,
          ratio: bestRatio,
          isAlias: false,
          headerIndex,
          aliasLengthDelta: bestAliasLengthDelta
        });
      }
    }
  }

  candidates.sort((a, b) => {
    if (a.isAlias !== b.isAlias) return a.isAlias ? -1 : 1;
    if (a.ratio !== b.ratio) return a.ratio - b.ratio;
    if (a.aliasLengthDelta !== b.aliasLengthDelta) {
      return a.aliasLengthDelta - b.aliasLengthDelta;
    }
    return a.headerIndex - b.headerIndex;
  });

  for (const { header, column } of candidates) {
    if (!claimed.has(column) && !result.has(header)) {
      result.set(header, column);
      claimed.add(column);
    }
  }

  return result;
};
